// components/Auth.js — ЧИСТАЯ ВЕРСИЯ БЕЗ SEO (2025 стандарт)
import React, { useState, useEffect } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  sendEmailVerification,
  sendPasswordResetEmail,
  signOut,
} from "firebase/auth";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";

const CONTINUE_URL = window.location.origin; // Используем текущий origin вместо фиксированного
const actionCodeSettings = { url: CONTINUE_URL, handleCodeInApp: false };
const googleProvider = new GoogleAuthProvider();

// Добавляем настройки для Google провайдера
googleProvider.setCustomParameters({
  prompt: 'select_account'
});
googleProvider.addScope('email');

export default function Auth({ user, setView }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isResetMode, setIsResetMode] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [googleError, setGoogleError] = useState(null);

  const toast = (msg, type = "info") =>
    window.dispatchEvent(new CustomEvent("showToast", { detail: { msg, type } }));

  // Обработка Google redirect (после редиректа)
  useEffect(() => {
    getRedirectResult(auth)
      .then((res) => {
        if (res?.user) {
          ensureFavorites(res.user.uid);
          toast("Вход через Google успешен!", "success");
        }
      })
      .catch((error) => {
        console.error("Google redirect error:", error);
        if (error.code === 'auth/popup-blocked') {
          toast("Разрешите всплывающие окна для входа через Google", "error");
        }
      });
  }, []);

  const ensureFavorites = async (uid) => {
    const ref = doc(db, "favorites", uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      await setDoc(ref, { list: [] });
    }
  };

  // Валидация email
  const validateEmail = (value) => {
    const email = value.trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && email.length <= 254;
  };

  // Жёсткая валидация пароля
  const validatePassword = (pwd) => {
    return {
      length: pwd.length >= 8,
      uppercase: /[A-Z]/.test(pwd),
      lowercase: /[a-z]/.test(pwd),
      digit: /\d/.test(pwd),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]/.test(pwd),
      noSpace: !/\s/.test(pwd),
    };
  };

  const getPasswordErrorMessage = () => {
    return "Пароль слабый! Требования:\n• Минимум 8 символов\n• Заглавная и строчная буква\n• Цифра\n• Спецсимвол (!@#$%^&* и др.)\n• Без пробелов";
  };

  // РЕГИСТРАЦИЯ
  const handleRegister = async () => {
    if (isLoading) return;
    setIsLoading(true);

    const trimmedEmail = email.trim();

    if (!trimmedEmail || !validateEmail(trimmedEmail)) {
      toast("Введите корректный email", "error");
      setIsLoading(false);
      return;
    }

    const pwdCheck = validatePassword(password);
    if (!Object.values(pwdCheck).every(Boolean)) {
      toast(getPasswordErrorMessage(), "error");
      setIsLoading(false);
      return;
    }

    try {
      const cred = await createUserWithEmailAndPassword(auth, trimmedEmail, password);
      await ensureFavorites(cred.user.uid);

      // Письмо отправляется, но вход сразу разрешён
      sendEmailVerification(cred.user, actionCodeSettings).catch(() => {});

      toast("Регистрация успешна! Добро пожаловать!", "success");
    } catch (err) {
      console.error("Registration error:", err);
      const msg =
        err.code === "auth/email-already-in-use"
          ? "Этот email уже зарегистрирован"
          : "Ошибка при регистрации";
      toast(msg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  // ВХОД
  const handleLogin = async () => {
    if (isLoading) return;
    setIsLoading(true);

    const trimmedEmail = email.trim();

    if (!trimmedEmail || !validateEmail(trimmedEmail)) {
      toast("Введите корректный email", "error");
      setIsLoading(false);
      return;
    }
    if (!password) {
      toast("Введите пароль", "error");
      setIsLoading(false);
      return;
    }

    try {
      await signInWithEmailAndPassword(auth, trimmedEmail, password);
      toast("Вход выполнен!", "success");
    } catch (err) {
      console.error("Login error:", err);
      toast("Неверный email или пароль", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // GOOGLE ВХОД - ИСПРАВЛЕННАЯ ВЕРСИЯ
  const handleGoogleLogin = async () => {
    if (isLoading) return;
    setIsLoading(true);
    setGoogleError(null);
    
    try {
      // Сначала пробуем popup
      const result = await signInWithPopup(auth, googleProvider);
      await ensureFavorites(result.user.uid);
      toast("Вход через Google успешен!", "success");
      
    } catch (error) {
      console.error("Google login error:", error);
      
      // Определяем тип ошибки
      if (error.code === 'auth/popup-blocked') {
        toast("Разрешите всплывающие окна для входа через Google", "error");
        setGoogleError("Всплывающие окна заблокированы браузером");
        
      } else if (error.code === 'auth/popup-closed-by-user') {
        toast("Вход отменён пользователем", "info");
        setGoogleError("Окно входа было закрыто");
        
      } else if (error.code === 'auth/unauthorized-domain') {
        toast("Домен не авторизован в Firebase Console", "error");
        setGoogleError("Проверьте настройки авторизованных доменов в Firebase");
        
      } else if (error.code === 'auth/operation-not-allowed') {
        toast("Метод входа не разрешён", "error");
        setGoogleError("Включите Google Sign-in в настройках Firebase");
        
      } else {
        // Для других ошибок пробуем redirect
        try {
          toast("Пробуем другой метод входа...", "info");
          await signInWithRedirect(auth, googleProvider);
          // После redirect браузер перезагрузится и выполнится useEffect выше
        } catch (redirectError) {
          console.error("Google redirect error:", redirectError);
          toast("Ошибка входа через Google", "error");
          setGoogleError(redirectError.message || "Неизвестная ошибка");
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  // СБРОС ПАРОЛЯ
  const handleResetPassword = async () => {
    if (isLoading) return;
    setIsLoading(true);

    const trimmedEmail = email.trim();
    if (!trimmedEmail || !validateEmail(trimmedEmail)) {
      toast("Введите корректный email", "error");
      setIsLoading(false);
      return;
    }

    try {
      await sendPasswordResetEmail(auth, trimmedEmail, actionCodeSettings);
      setResetEmailSent(true);
      toast("Письмо отправлено! Проверьте почту и СПАМ", "success");
    } catch (error) {
      console.error("Reset password error:", error);
      if (error.code === 'auth/user-not-found') {
        toast("Пользователь не найден", "error");
      } else {
        toast("Ошибка при отправке письма", "error");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Поддержка Enter
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !isLoading) {
      if (isResetMode) handleResetPassword();
      else if (user) return;
      else if (password) handleLogin();
      else handleRegister();
    }
  };

  // ВЫХОД
  const handleLogout = async () => {
    await signOut(auth);
    setView("tools");
    toast("Вы вышли из аккаунта", "info");
  };

  return (
    <div
      style={{
        padding: "24px",
        maxWidth: "400px",
        margin: "0 auto",
        color: "#fff",
        animation: "zoomIn 0.6s ease-out",
      }}
      onKeyDown={handleKeyPress}
      tabIndex={-1}
    >
      <h2 style={{ textAlign: "center", marginBottom: "30px", fontSize: "22px" }}>
        {user
          ? "Профиль"
          : isResetMode
          ? "Восстановление пароля"
          : "Вход или регистрация"}
      </h2>

      {/* Показываем ошибку Google, если есть */}
      {googleError && (
        <div style={{
          padding: "12px",
          marginBottom: "20px",
          background: "rgba(255, 68, 68, 0.1)",
          border: "1px solid #ff4444",
          borderRadius: "8px",
          color: "#ff4444",
          fontSize: "14px",
        }}>
          <strong>Ошибка Google:</strong> {googleError}
        </div>
      )}

      {/* EMAIL */}
      {!user && !resetEmailSent && (
        <div style={{ marginBottom: "18px" }}>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
            style={{
              width: "100%",
              padding: "14px",
              borderRadius: "12px",
              border: "1px solid #555",
              background: "#1a1a1a",
              color: "#fff",
              fontSize: "16px",
              outline: "none",
            }}
          />
        </div>
      )}

      {/* ПАРОЛЬ */}
      {!isResetMode && !resetEmailSent && !user && (
        <div style={{ marginBottom: "20px" }}>
          <input
            type="password"
            placeholder="Пароль (мин. 8 символов)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isLoading}
            style={{
              width: "100%",
              padding: "14px",
              borderRadius: "12px",
              border: "1px solid #555",
              background: "#1a1a1a",
              color: "#fff",
              fontSize: "16px",
              outline: "none",
            }}
          />
        </div>
      )}

      {/* КНОПКИ */}
      {resetEmailSent ? (
        <p style={{ textAlign: "center", color: "#4ade80", fontSize: "18px" }}>
          Письмо отправлено на <strong>{email}</strong>
        </p>
      ) : isResetMode ? (
        <>
          <button
            onClick={handleResetPassword}
            disabled={isLoading}
            style={{
              width: "100%",
              padding: "16px",
              background: "#4ade80",
              color: "#fff",
              border: "none",
              borderRadius: "12px",
              fontSize: "17px",
              cursor: isLoading ? "not-allowed" : "pointer",
              opacity: isLoading ? 0.7 : 1,
            }}
          >
            {isLoading ? "Отправка..." : "Отправить письмо"}
          </button>
          <button
            onClick={() => {
              setIsResetMode(false);
              setResetEmailSent(false);
              setGoogleError(null);
            }}
            style={{
              marginTop: "12px",
              background: "none",
              border: "none",
              color: "#aaa",
              fontSize: "15px",
              cursor: "pointer",
            }}
          >
            ← Назад
          </button>
        </>
      ) : user ? (
        <div style={{ textAlign: "center" }}>
          <p style={{ fontSize: "14px", marginBottom: "20px", lineHeight: "1.5" }}>
            Привет, <strong>{user.email}</strong>!
            <br />
            Теперь твои стили можно сохранить.
          </p>
          <button
            onClick={handleLogout}
            style={{
              padding: "14px 40px",
              background: "#ff4444",
              color: "#fff",
              border: "none",
              borderRadius: "12px",
              fontSize: "17px",
              cursor: "pointer",
            }}
          >
            Выйти
          </button>
        </div>
      ) : (
        <>
          <div style={{ display: "flex", gap: "12px", marginBottom: "16px" }}>
            <button
              onClick={handleRegister}
              disabled={isLoading}
              style={{
                flex: 1,
                padding: "16px",
                background: "#4ade80",
                color: "#fff",
                border: "none",
                borderRadius: "12px",
                fontSize: "17px",
                opacity: isLoading ? 0.7 : 1,
                cursor: isLoading ? "not-allowed" : "pointer",
              }}
            >
              {isLoading ? "..." : "Регистрация"}
            </button>
            <button
              onClick={handleLogin}
              disabled={isLoading}
              style={{
                flex: 1,
                padding: "16px",
                background: "#333",
                color: "#fff",
                border: "1px solid #555",
                borderRadius: "12px",
                fontSize: "17px",
                opacity: isLoading ? 0.7 : 1,
                cursor: isLoading ? "not-allowed" : "pointer",
              }}
            >
              {isLoading ? "..." : "Войти"}
            </button>
          </div>

          {/* Кнопка Google с улучшенным UI */}
          <div style={{ position: "relative", marginBottom: "24px" }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "16px"
            }}>
              <div style={{ flex: 1, height: "1px", background: "rgba(255,255,255,0.1)" }}></div>
              <span style={{ margin: "0 12px", color: "#aaa", fontSize: "14px" }}>или</span>
              <div style={{ flex: 1, height: "1px", background: "rgba(255,255,255,0.1)" }}></div>
            </div>

            <button
              onClick={handleGoogleLogin}
              disabled={isLoading}
              style={{
                width: "100%",
                padding: "14px",
                background: "#fff",
                color: "#000",
                border: "1px solid #ddd",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "12px",
                fontWeight: "500",
                opacity: isLoading ? 0.7 : 1,
                cursor: isLoading ? "not-allowed" : "pointer",
                transition: "all 0.3s",
              }}
              onMouseEnter={(e) => !isLoading && (e.currentTarget.style.transform = "translateY(-2px)")}
              onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 6.75c1.63 0 3.06.56 4.21 1.65l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              {isLoading ? "Подключение..." : "Войти через Google"}
            </button>
          </div>

          <div style={{ textAlign: "center", marginTop: "24px" }}>
            <button
              onClick={() => setIsResetMode(true)}
              style={{
                background: "none",
                border: "none",
                color: "#4ade80",
                textDecoration: "underline",
                cursor: "pointer",
                fontSize: "15px",
              }}
            >
              Забыли пароль?
            </button>
          </div>
        </>
      )}
    </div>
  );
}